import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, writeBatch, query, where, getDocs, setDoc } from '../firebase';
import { Item, StockRecord, InventoryTransfer, InventoryMove } from '../types';
import { v4 as uuidv4 } from 'uuid';

/** Helper to get or create a default location */
async function getOrCreateLocation(orgId: string, warehouseId: string | null, type: 'internal' | 'customer' | 'vendor' | 'scrap' | 'transit', name: string) {
  const qArgs = warehouseId 
    ? [where('orgId', '==', orgId), where('warehouseId', '==', warehouseId), where('type', '==', type)] 
    : [where('orgId', '==', orgId), where('type', '==', type)];
  
  const locQ = query(collection(db, 'inventory_locations'), ...qArgs);
  const snap = await getDocs(locQ);
  if (!snap.empty) {
    return snap.docs[0].id;
  }
  
  // Create it
  const locRef = doc(collection(db, 'inventory_locations'));
  await setDoc(locRef, {
    orgId,
    ...(warehouseId && { warehouseId }),
    name,
    type,
    status: 'active',
    createdAt: serverTimestamp()
  });
  return locRef.id;
}

export async function processStockMove(
  orgId: string,
  itemId: string,
  quantity: number,
  sourceLocationId: string,
  destinationLocationId: string,
  reference: string = 'Automatic Move',
  operationType: 'receipt' | 'delivery' | 'internal' = 'internal'
) {
  if (quantity === 0) return;

  const batch = writeBatch(db);

  const transferRef = doc(collection(db, 'inventory_transfers'));
  batch.set(transferRef, {
    orgId,
    reference,
    sourceLocationId,
    destinationLocationId,
    status: 'done',
    operationType,
    createdAt: serverTimestamp()
  } as Partial<InventoryTransfer>);

  const moveRef = doc(collection(db, 'inventory_moves'));
  batch.set(moveRef, {
    orgId,
    transferId: transferRef.id,
    productId: itemId,
    quantityDemand: quantity,
    quantityReserved: 0,
    quantityDone: quantity,
    sourceLocationId,
    destinationLocationId,
    status: 'done',
    createdAt: serverTimestamp()
  } as Partial<InventoryMove>);
  
  // Apply Quants for Source
  if (sourceLocationId && quantity > 0) {
    const srcQuantQ = query(collection(db, 'inventory_quants'), where('orgId', '==', orgId), where('productId', '==', itemId), where('locationId', '==', sourceLocationId));
    const srcSnap = await getDocs(srcQuantQ);
    if (!srcSnap.empty) {
       const qdoc = srcSnap.docs[0];
       batch.update(qdoc.ref, { 
         quantityOnHand: (qdoc.data().quantityOnHand || 0) - quantity,
         updatedAt: serverTimestamp() 
       });
    } else {
       const newQRef = doc(collection(db, 'inventory_quants'));
       batch.set(newQRef, {
         orgId, productId: itemId, locationId: sourceLocationId, quantityOnHand: -quantity, quantityReserved: 0, updatedAt: serverTimestamp()
       });
    }
  }

  // Apply Quants for Dest
  if (destinationLocationId && quantity > 0) {
    const dstQuantQ = query(collection(db, 'inventory_quants'), where('orgId', '==', orgId), where('productId', '==', itemId), where('locationId', '==', destinationLocationId));
    const dstSnap = await getDocs(dstQuantQ);
    if (!dstSnap.empty) {
       const qdoc = dstSnap.docs[0];
       batch.update(qdoc.ref, { 
         quantityOnHand: (qdoc.data().quantityOnHand || 0) + quantity,
         updatedAt: serverTimestamp() 
       });
    } else {
       const newQRef = doc(collection(db, 'inventory_quants'));
       batch.set(newQRef, {
         orgId, productId: itemId, locationId: destinationLocationId, quantityOnHand: quantity, quantityReserved: 0, updatedAt: serverTimestamp()
       });
    }
  }

  await batch.commit();
}

/**
 * Handles FIFO stock reduction.
 * Consumes stock from the oldest stock records first.
 * Returns the total cost of the consumed stock.
 */
export async function reduceStockFIFO(itemId: string, quantityToReduce: number, warehouseId: string): Promise<number> {
  const itemRef = doc(db, 'items', itemId);
  const itemSnap = await getDoc(itemRef);
  
  if (!itemSnap.exists()) {
    throw new Error('Item not found');
  }

  const itemData = itemSnap.data() as Item;
  const stockRecords = [...(itemData.stockRecords || [])];
  
  // Filter records for the specific warehouse and sort by lastUpdated
  const relevantRecords = stockRecords
    .filter(r => r.warehouseId === warehouseId && r.quantity > 0)
    .sort((a, b) => {
       // Prioritize expiry date if available, then by creation date/updated date
       if (a.expiryDate && b.expiryDate) return a.expiryDate.localeCompare(b.expiryDate);
       if (a.expiryDate) return -1;
       if (b.expiryDate) return 1;
       
       const timeA = a.lastUpdated instanceof Date ? a.lastUpdated.getTime() : (a.lastUpdated?.seconds ? a.lastUpdated.seconds * 1000 : 0);
       const timeB = b.lastUpdated instanceof Date ? b.lastUpdated.getTime() : (b.lastUpdated?.seconds ? b.lastUpdated.seconds * 1000 : 0);
       return timeA - timeB;
    });

  let remainingToReduce = quantityToReduce;
  let totalCost = 0;

  for (const record of relevantRecords) {
    if (remainingToReduce <= 0) break;

    const consumeQty = Math.min(record.quantity, remainingToReduce);
    record.quantity -= consumeQty;
    const unitCost = record.costPrice || itemData.costPrice || 0;
    totalCost += consumeQty * unitCost;
    remainingToReduce -= consumeQty;
  }

  if (remainingToReduce > 0) {
    // If not enough stock in records, fallback to current cost price
    totalCost += remainingToReduce * (itemData.costPrice || 0);

    // Also deduct from first record or add a negative record to track backorder
    if (relevantRecords.length > 0) {
      relevantRecords[0].quantity -= remainingToReduce;
    } else {
      stockRecords.push({
        id: uuidv4(),
        warehouseId,
        warehouseName: 'Main Warehouse',
        quantity: -remainingToReduce,
        costPrice: itemData.costPrice || 0,
        lastUpdated: new Date()
      });
    }
  }

  // Update the item doc with new stock records and overall quantity
  await updateDoc(itemRef, {
    stockRecords: stockRecords,
    quantity: (itemData.quantity || 0) - quantityToReduce,
    [`storeStock.${warehouseId}`]: (itemData.storeStock?.[warehouseId] || 0) - quantityToReduce,
    updatedAt: serverTimestamp()
  });

  // WMS integration
  try {
    const srcLoc = await getOrCreateLocation(itemData.orgId, warehouseId, 'internal', 'WH/Stock');
    const dstLoc = await getOrCreateLocation(itemData.orgId, null, 'customer', 'Customers');
    await processStockMove(itemData.orgId, itemId, quantityToReduce, srcLoc, dstLoc, 'Legacy Reduce (Delivery)', 'delivery');
  } catch (e) {
    console.warn("WMS async sync failed in reduceStockFIFO: ", e);
  }

  return totalCost;
}

/**
 * Adds stock as a new FIFO record.
 */
export async function addStockFIFO(itemId: string, quantity: number, costPrice: number, warehouseId: string, warehouseName: string, lotNumber?: string, expiryDate?: string) {
  const itemRef = doc(db, 'items', itemId);
  const itemSnap = await getDoc(itemRef);
  
  if (!itemSnap.exists()) {
    throw new Error('Item not found');
  }

  const itemData = itemSnap.data() as Item;
  const stockRecords = [...(itemData.stockRecords || [])];

  const newRecord: StockRecord = {
    id: uuidv4(),
    warehouseId,
    warehouseName,
    lotNumber,
    expiryDate,
    quantity,
    costPrice,
    lastUpdated: new Date() 
  };

  stockRecords.push(newRecord);

  await updateDoc(itemRef, {
    stockRecords: stockRecords,
    quantity: (itemData.quantity || 0) + quantity,
    [`storeStock.${warehouseId}`]: (itemData.storeStock?.[warehouseId] || 0) + quantity,
    updatedAt: serverTimestamp()
  });

  // WMS integration 
  try {
    const srcLoc = await getOrCreateLocation(itemData.orgId, null, 'vendor', 'Vendors');
    const dstLoc = await getOrCreateLocation(itemData.orgId, warehouseId, 'internal', 'WH/Stock');
    await processStockMove(itemData.orgId, itemId, quantity, srcLoc, dstLoc, 'Legacy Add (Receipt)', 'receipt');
  } catch (e) {
    console.warn("WMS async sync failed in addStockFIFO: ", e);
  }
}

/**
 * Calculates the FIFO cost impact for a given quantity of an item.
 * Useful for COGS calculations without actually modifying stock records yet.
 */
export async function getFIFOImpact(itemId: string, quantity: number, warehouseId?: string): Promise<{ totalCost: number, details: any[] }> {
  try {
    const itemDoc = await getDoc(doc(db, 'items', itemId));
    if (!itemDoc.exists()) return { totalCost: 0, details: [] };

    const itemData = itemDoc.data() as Item;
    const records = [...(itemData.stockRecords || [])];

    let remaining = quantity;
    let totalCost = 0;
    const details: any[] = [];

    const eligibleRecords = records
      .filter(r => !warehouseId || r.warehouseId === warehouseId)
      .sort((a, b) => {
        if (a.expiryDate && b.expiryDate) return a.expiryDate.localeCompare(b.expiryDate);
        if (a.expiryDate) return -1;
        if (b.expiryDate) return 1;
        
        const timeA = a.lastUpdated instanceof Date ? a.lastUpdated.getTime() : (a.lastUpdated?.seconds ? a.lastUpdated.seconds * 1000 : 0);
        const timeB = b.lastUpdated instanceof Date ? b.lastUpdated.getTime() : (b.lastUpdated?.seconds ? b.lastUpdated.seconds * 1000 : 0);
        return timeA - timeB;
      });

    for (const record of eligibleRecords) {
      if (remaining <= 0) break;
      const consume = Math.min(record.quantity, remaining);
      if (consume <= 0) continue;

      const unitCost = record.costPrice || itemData.costPrice || 0;
      const cost = consume * unitCost;

      totalCost += cost;
      remaining -= consume;
      details.push({
        lotNumber: record.lotNumber,
        quantityUsed: consume,
        costBasis: unitCost,
        total: cost
      });
    }

    if (remaining > 0) {
      const fallbackCost = itemData.costPrice || 0;
      totalCost += remaining * fallbackCost;
      details.push({
        lotNumber: 'BACKORDER',
        quantityUsed: remaining,
        costBasis: fallbackCost,
        total: remaining * fallbackCost
      });
    }

    return { totalCost, details };
  } catch (error) {
    console.error("Error calculating FIFO impact:", error);
    return { totalCost: 0, details: [] };
  }
}
